import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, ReplaySubject, map } from 'rxjs';
import {
  AgreementTemplate,
  AgreementView,
} from 'src/app/admin/data-access/agreement.model';
import { APIResponse } from 'src/app/shared/data-access/api-response.model';
import { environment } from 'src/environments/environment';
import {catchApiError, catchErrorWith} from "../../shared/utils/api-error";
import {encodeParam} from "../../shared/utils/query-string";

@Injectable({
  providedIn: 'root',
})
export class AgreementService {
  private httpClient = inject(HttpClient);

  requiredAgreements: AgreementView[] = [];
  optionalAgreements: AgreementView[] = [];

  // TODO: crate mechanism that feeds agreements one by one to the terms page after the previous one is accepted
  nextRequiredAgreement$: ReplaySubject<AgreementView> =
    new ReplaySubject<AgreementView>();

  nextOptionalAgreements$: ReplaySubject<AgreementView[]> = new ReplaySubject<
    AgreementView[]
  >();

  fetchAgreements(): Observable<AgreementTemplate[]> {
    const apiUrl = `${environment.baseUrl}/agreements/manage/listcurrentfuture`;

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        if (!response.success) {
          return [];
        }

        return response.result;
      })
    );
  }

  // list required agreements
  listRequiredAgreements(): Observable<AgreementView[]> {
    const apiUrl = `${environment.baseUrl}/agreements/pending/listrequired`;
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        if (response.success && response.msg !== 'No pending agreements') {
          return response.result;
        }
        return [];
      })
    );
  }

  // list optional agreements
  listOptionalAgreements(): Observable<AgreementView[]> {
    const apiUrl = `${environment.baseUrl}/agreements/pending/listoptional`;
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        if (response.success && response.msg !== 'No pending agreements') {
          return response.result;
        }
        return [];
      }),
      catchErrorWith([] as AgreementView[])
    );
  }

  // respond to required agreement
  acceptRequiredAgreement(agreement_id: string): void {
    const apiUrl = `${environment.baseUrl}/agreements/pending/respond?accept_ids=${encodeParam(agreement_id)}`;

    const httpParams = new HttpParams().set('accept_ids', agreement_id);
    // TODO: httpParams not being recognized by the backend for some reason

    // Call API and on success, remove the agreement from the list and serve next one
    this.httpClient
      .post<APIResponse>(apiUrl, {})
      .subscribe((response) => {
        if (response.success) {
          this.requiredAgreements = this.requiredAgreements.slice(1);
          if (this.requiredAgreements.length > 0) {
            this.nextRequiredAgreement$.next(this.requiredAgreements[0]);
          } else {
            // Send null object to the terms page to indicate that there are no more agreements to accept
            this.nextRequiredAgreement$.complete();
          }
        }
      });
  }

  // respond to optional agreements
  acceptOptionalAgreements(accepted_ids: string, rejected_ids: string): void {
    const apiUrl = `${environment.baseUrl}/agreements/pending/respond?accept_ids=${encodeParam(accepted_ids)}&reject_ids=${encodeParam(rejected_ids)}`;


    this.httpClient.post<APIResponse>(apiUrl, {}).subscribe((response) => {
      if(response.success) {
        this.optionalAgreements = [];
        this.nextOptionalAgreements$.complete();
      }
    })
  }

  // list all agreements for agreement management
  listAllAgreements(): Observable<AgreementTemplate[]> {
    const apiUrl = `${environment.baseUrl}/agreements/manage/listcurrentfuture`;
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        if (response.success) {
          return response.result;
        }
        return [];
      })
    );
  }

  // Get the details of an agreement from provided id
  agreementDetails(id: string): Observable<AgreementView> {
    const apiUrl = `${environment.baseUrl}/agreements/details?agreement_id=`+encodeParam(id);
    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        if (response.success) {
          return response.result;
        }
        return;
      })
    );
  }

  createAgreement(name: string, agreement_text: string, notice_text: string, required: boolean, retire_id?: string): Observable<APIResponse> {

    // TODO: fix the 431 for agreement text when the text is very long. Fix using the body in the post

    let paramString = 'name='+encodeParam(name)+'&notice_text='+encodeParam(notice_text);

    if(retire_id != null) {
      paramString = paramString+'&retire_id='+encodeParam(retire_id);
    }

    if(!required) {
      paramString = paramString+'&reject_text=Decline';
    }

    paramString = paramString+'&accept_text=Accept and Continue';

    const apiUrl = `${environment.baseUrl}/agreements/manage/create?` + paramString;

    const body = new HttpParams().set('agreement_text', agreement_text)

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map( (response) => {
        return response
      }),
      catchApiError()
    );
  }

  retireAgreement(id: number): Observable<boolean> {
    const apiUrl = `${environment.baseUrl}/agreements/manage/retire?agreement_ids=` + encodeParam(id);

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response.success
      }),
      catchErrorWith(false)
    )
  }

  enableAgreement(id: number): Observable<boolean> {
    const apiUrl = `${environment.baseUrl}/agreements/manage/enable?agreement_ids=` + encodeParam(id);

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response.success
      }),
      catchErrorWith(false)
    )
  }
}
